import { z } from "zod";
import i18n from "@/i18n";

/**
 * Zod error option with a translated message. The key is resolved each time the rule fails (not when the
 * schema is created), so messages follow locale switches and schemas can live at module scope.
 *
 * @example z.string().max(64, msg("integrations.rules.maxLength", { count: 64 }))
 * @param key i18n key
 * @param params interpolation params for the message
 */
export function msg(key: string, params: Record<string, unknown> = {}): { error: () => string } {
  return { error: () => i18n.global.t(key, params) };
}

/*
 * Safety net: translated fallback for every rule that has no message of its own (Zod's defaults are English,
 * e.g. "Invalid input: expected string, received undefined"). Explicit messages always take precedence.
 * An empty value (untouched field) reads as "required", anything else as a generic "invalid".
 */
z.config({
  customError: (issue) => {
    const input = issue.input;
    const isEmpty = input === undefined || input === null || input === "" || (Array.isArray(input) && !input.length);
    if (isEmpty && (issue.code === "invalid_type" || issue.code === "too_small")) return i18n.global.t("validation.required");
    return i18n.global.t("validation.invalid");
  },
});

/** A Zod error option (e.g. from `msg()`) or a literal message (only for admin-only, untranslated screens) */
export type ErrorOption = { error: () => string } | string;

/**
 * Required text: must be a non-empty string. `null`/`undefined` (untouched fields) report the same message.
 * Whitespace-only input counts as filled, like Vuetify's `v => !!v` did; add `.trim()` where that matters.
 * @param message error option, defaults to the generic "required" message
 */
export function zRequired(message: ErrorOption = msg("validation.required")) {
  return z.string(message).min(1, message);
}

/**
 * Optional text without further rules: accepts `''`, `null` and `undefined` and always yields a string.
 * For optional fields that must satisfy a rule when filled, use `zOptional()`.
 */
export function zOptionalString() {
  return z.string().nullish().transform((v) => v ?? "");
}

/**
 * Makes a rule optional: empty (`''`, whitespace-only, `null`, `undefined`) passes, anything else must satisfy `schema`.
 * @example zOptional(zUrl()) // may be left blank, but if filled it must be a URL
 * @param schema rule for non-empty values
 */
export function zOptional<T extends z.ZodType>(schema: T) {
  const isEmpty = (value: unknown) => value === null || (typeof value === "string" && value.trim() === "");
  return z.preprocess((value) => (isEmpty(value) ? undefined : value), schema.optional());
}

/**
 * E-mail address. Uses Zod's (reasonably strict) e-mail check, and rejects empty input with the same message.
 * @param message error option, defaults to the generic "invalid e-mail" message
 */
export function zEmail(message: ErrorOption = msg("validation.email")) {
  return z.email(message);
}

/**
 * Absolute web URL (http/https only). Rejects empty input with the same message.
 * @param message error option, defaults to the generic "invalid URL" message
 * @param options.httpsOnly only accept `https://`
 */
export function zUrl(message: ErrorOption = msg("validation.url"), options: { httpsOnly?: boolean } = {}) {
  const protocol = options.httpsOnly ? /^https$/ : /^https?$/;
  return z.url({ protocol, hostname: z.regexes.domain, ...toParams(message) });
}

/**
 * Link target as accepted by chat buttons: http(s)/ftp(s) URL, `mailto:` or `tel:` link, shorter than 2083
 * characters (legacy IE URL limit). Mirrors `mustBeUrlOrMailto` in `@/utils/validationRules`.
 * @param message error option
 */
export function zLinkTarget(message: ErrorOption) {
  return z.string(message).refine((value) => {
    let regex = /^(http|https|ftp|ftps):\/\/[^ "]+$/;
    if (value.includes("tel")) regex = /^tel:[+]?[0-9]+$/;
    if (value.includes("mailto")) regex = /^mailto:[^@]+@[^@]+\.[^@]+$/;
    return value.length < 2083 && regex.test(value);
  }, message);
}

/**
 * String that must be valid JSON (any JSON value). Empty input fails.
 * @param message error option
 */
export function zJsonString(message: ErrorOption) {
  return z.string(message).refine((value) => {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }, message);
}

/** Normalizes an `ErrorOption` into the params object shape Zod's format validators expect */
function toParams(message: ErrorOption): { error: string | (() => string) } {
  return typeof message === "string" ? { error: message } : message;
}
